/**
 * The group trip to Varanasi for the 103rd Varshikotsav at Swarved
 * Mahamandir, 14 and 15 December 2026: the interest form at
 * vyaustralia.org/varanasi. Shared so the page, the backend's validation,
 * its emails and the team app's tallies offer exactly the same choices.
 */

/** Stored with every answer in `survey_responses`. */
export const VARANASI_TRIP_SURVEY = "varanasi-december-2026";

/** Who is planning the trip and takes the questions about it. */
export const VARANASI_TRIP_CONTACT = {
  name: "Alok Kumar",
  phone: "0478 476 565",
  phoneHref: "tel:+61478476565",
} as const;

export const AUSTRALIAN_STATES = [
  "NSW",
  "VIC",
  "QLD",
  "WA",
  "SA",
  "TAS",
  "ACT",
  "NT",
] as const;

/** "Will you join the main programme on 14 and 15 December?" */
export const TRIP_MAIN_PROGRAMME = ["Yes", "Not sure yet", "No"] as const;

/** Arriving earlier than the programme, staying on after it, both, or
 *  neither. */
export const TRIP_STAY_IDS = ["main-only", "earlier", "later", "both"] as const;

export type TripStayId = (typeof TRIP_STAY_IDS)[number];

export const TRIP_STAY_LABEL = {
  "main-only": "Just the main programme dates",
  earlier: "Arrive earlier",
  later: "Stay on after",
  both: "Arrive earlier and stay on",
} satisfies Record<TripStayId, string>;

/** Whether a stay plan goes beyond the programme, so the form asks where
 *  else they'd like to go. */
export function extendsTheTrip(stay: TripStayId | null): boolean {
  return stay === "earlier" || stay === "later" || stay === "both";
}

/** The places and experiences people can tick. */
export const TRIP_INTEREST_IDS = [
  "sightseeing",
  "ganga-aarti",
  "boat-ghats",
  "kashi-vishwanath",
  "sarnath",
  "gaya-pind-daan",
  "wellness-retreat",
  "other",
] as const;

export type TripInterestId = (typeof TRIP_INTEREST_IDS)[number];

export const TRIP_INTEREST_LABEL = {
  sightseeing: "Varanasi sightseeing",
  "ganga-aarti": "Ganga Aarti",
  "boat-ghats": "A boat ride along the Ghats",
  "kashi-vishwanath": "Kashi Vishwanath",
  sarnath: "Sarnath",
  "gaya-pind-daan": "Gaya Ji and Pind Daan",
  "wellness-retreat": "A wellness or yoga retreat",
  other: "Other places",
} satisfies Record<TripInterestId, string>;

/** A phone number as people type it: digits with spaces, dashes, brackets
 *  or a leading +, at least eight digits and at most thirty characters. */
export function looksLikePhone(value: string): boolean {
  const v = value.trim();
  return (
    v.length <= 30 &&
    /^\+?[\d\s().-]+$/.test(v) &&
    v.replace(/\D/g, "").length >= 8
  );
}

/** Most people one form can list, the person filling it in included. */
export const TRIP_GROUP_MAX = 20;

/** Longest free-text answer the form accepts. */
export const TRIP_TEXT_MAX = 1000;

/** The range the date pickers allow: the weeks around the trip. */
export const TRIP_DATE_MIN = "2026-11-01";
export const TRIP_DATE_MAX = "2027-03-31";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Nights between an arrival and a departure date (`YYYY-MM-DD`), or
 *  null when either is missing or they run backwards. */
export function tripNights(
  arrival: string | null,
  departure: string | null,
): number | null {
  if (!arrival || !departure) {
    return null;
  }
  const from = Date.parse(`${arrival}T00:00:00Z`);
  const until = Date.parse(`${departure}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(until) || until < from) {
    return null;
  }
  return Math.round((until - from) / DAY_MS);
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** "2026-12-13" → "Sun 13 Dec 2026", read as a calendar date (no time
 *  zone can move it a day) and spelled out by hand, so the page, the
 *  emails and the team app print it the same way on any runtime. */
export function tripDateLabel(iso: string): string {
  const at = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(at)) {
    return iso;
  }
  const d = new Date(at);
  return `${WEEKDAYS[d.getUTCDay()] ?? ""} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()] ?? ""} ${d.getUTCFullYear()}`;
}
